import sortBy from 'lodash/sortBy'
import {maybe} from '@freckle/maybe'
import type {ParserT} from '@freckle/parser'
import {array, number, record} from '@freckle/parser'
import type {TranslateT} from '@freckle/educator-entities/ts/common/helpers/translate'
import type {RlDomainT} from '@freckle/educator-entities/ts/common/models/rl-domain'
import {filterDomainsByGrade} from '@freckle/educator-entities/ts/common/models/rl-domain'
import type {RlStandardWithDomainT} from '@freckle/educator-entities/ts/common/models/rl-standard'
import type {RlStandardSetT} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import * as RlDomainStandard from './rl-domain-standard'
import type {RlDomainStandardT} from './rl-domain-standard'

export type DehydratedMathAssessmentTopicT = {
  schoolGrade: number
  domainStandards: Array<RlDomainStandard.DehydratedRlDomainStandard>
}

export type MathAssessmentTopicT = {
  schoolGrade: number
  domainStandards: Array<RlDomainStandardT>
}

export const parseDehydrated: ParserT<DehydratedMathAssessmentTopicT> = record({
  schoolGrade: number(),
  domainStandards: array(RlDomainStandard.parseDehydrated),
})

export function toKey(topic: DehydratedMathAssessmentTopicT): string {
  const {schoolGrade, domainStandards} = topic
  const keys = sortBy(domainStandards.map(domainStandard => RlDomainStandard.toKey(domainStandard)))
  return `${schoolGrade}-${keys.join('-')}`
}

export const hydrateTopic = (
  dehydratedTopic: DehydratedMathAssessmentTopicT,
  rlDomains: Array<RlDomainT>,
  rlStandards: Array<RlStandardWithDomainT>,
): MathAssessmentTopicT => ({
  ...dehydratedTopic,
  domainStandards: dehydratedTopic.domainStandards.flatMap(ds => {
    const mDomain = rlDomains.find(d => d.id === ds.domain.id)
    const standardIds = ds.standards.map(s => s.id)
    const standards = rlStandards.filter(s => standardIds.includes(s.id))

    return mDomain === undefined || (standards.length === 0 && standardIds.length > 0)
      ? []
      : [
          {
            domain: mDomain,
            standards,
          },
        ]
  }),
})

export function format(
  dehydratedTopic: DehydratedMathAssessmentTopicT,
  mathStandardSet: RlStandardSetT,
  rlDomains: Array<RlDomainT>,
  rlStandards: Array<RlStandardWithDomainT>,
  t: TranslateT,
): string {
  const {schoolGrade, domainStandards} = hydrateTopic(dehydratedTopic, rlDomains, rlStandards)

  const {domainsLabel, standardsLabel} = mathStandardSet

  if (domainStandards.length > 1) {
    const allDomainsForGrade = filterDomainsByGrade(rlDomains, schoolGrade)
    const standards = domainStandards.flatMap(ds => ds.standards)

    return domainStandards.length === allDomainsForGrade.length && standards.length === 0
      ? t('common|ASSESSMENT_TOPIC_FORMAT_ALL_DOMAINS_FOR_GRADE', {
          domainsLabel,
          schoolGrade,
        })
      : t('common|ASSESSMENT_TOPIC_FORMAT_MULTIPLE_DOMAINS_AND_STANDARDS', {
          domainsLabel,
          standardsLabel,
        })
  } else {
    return maybe(
      () => t('common|ASSESSMENT_TOPIC_FORMAT_NO_DOMAINS', {domainsLabel}),
      ({domain, standards}) => {
        if (standards.length > 1) {
          return t('common|ASSESSMENT_TOPIC_FORMAT_MULTIPLE_STANDARDS', {
            domainName: domain.name,
            mdash,
            standardsLabel,
          })
        } else {
          return maybe(
            () =>
              //The convention being used by the backend is: if the domain is specified but
              //the standards array is empty, it means the entire domain has been selected.
              t('common|ASSESSMENT_TOPIC_FORMAT_ALL_STANDARDS', {
                domainName: domain.name,
                mdash,
                standardsLabel,
              }),
            ({name: standardName}) =>
              t('common|ASSESSMENT_TOPIC_FORMAT_DOMAIN_AND_STANDARD', {
                domainName: domain.name,
                mdash,
                standardName,
              }),
            standards[0],
          )
        }
      },
      domainStandards[0],
    )
  }
}

export function formatDomains(
  domains: Array<RlDomainT>,
  mathStandardSet: RlStandardSetT,
  t: TranslateT,
): string {
  const {domainsLabel} = mathStandardSet

  if (domains.length > 1) {
    return t('common|ASSESSMENT_TOPIC_FORMAT_MULTIPLE_DOMAINS', {domainsLabel})
  } else {
    return maybe(
      () => t('common|ASSESSMENT_TOPIC_FORMAT_NO_DOMAINS', {domainsLabel}),
      domain => `${domain.name}`,
      domains[0],
    )
  }
}

const mdash = '\u2014'
